// Iter 544 — LEADERBOARD MOCKUP SHOPEE (seluruh PIC Mockup Shopee).
//
// Mandat Owner:
//   • Scope: SEMUA operator PIC Mockup Shopee (team="Shopee" ATAU is_shopee_designer=True).
//     Daftar dinamis dari master Operator; tidak hardcode.
//   • Setiap PIC & Owner dapat melihat seluruh peringkat.
//   • Metrik utama: jumlah FIX (status mockup = FIX final).
//   • Revisi sebagai indikator terpisah (bukan ditambahkan ke FIX).
//   • Toggle periode: Hari Ini / 7 Hari / 30 Hari. Default 7.
//   • Tampilkan selisih thdp peringkat 1 (gap_from_top).
//   • WIB. Hari tanpa pekerjaan = 0.
//
// HANYA untuk Mockup Shopee. BUKAN Mockup Offline. BUKAN Desain Bordir.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import "./leaderboard_mockup_shopee.css";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Palet 8 warna — cukup banyak utk tim mockup kecil-sedang; wrap jika > 8.
const BAR_COLORS = [
  "#f97316", "#2563eb", "#10b981", "#a855f7",
  "#ec4899", "#eab308", "#06b6d4", "#f43f5e",
];

function _shortDate(d) {
  if (!d || typeof d !== "string") return "";
  const parts = d.split("-");
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}`;
}

function RankBadge({ rank, size = 18 }) {
  const label = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;
  return <span style={{ fontSize: size, lineHeight: 1 }} aria-label={`Peringkat ${rank}`}>{label}</span>;
}

// Ringkasan 1 baris: peringkat PIC login + pemimpin; data & rumus sama dgn leaderboard lengkap.
function CompactLeaderboard({ data, days, meId, meName, loading, err, onExpand, testIdPrefix }) {
  const ds = data?.designers || [];
  const me = ds.find(d => d.id === meId || (d._alias_ids || []).includes(meId))
    || (meName ? ds.find(d => (d.name || "").toLowerCase() === meName.toLowerCase()) : null);
  const top = ds[0];
  const period = days === 0 ? "Semua Riwayat" : days === 1 ? "Hari Ini" : `${days} Hari`;
  return <section className="mlb-compact" data-testid={`${testIdPrefix}-compact`}>
    <div className="mlb-c-main">
      <div className="mlb-c-title"><b>Performa Desainer</b><span>{period} · WIB</span></div>
      {loading && !data ? <span className="mlb-c-muted">Memuat…</span>
        : err ? <span className="mlb-c-muted" data-testid={`${testIdPrefix}-err`}>{err}</span>
        : <div className="mlb-c-stats">
          {me && <span className="mlb-c-me" data-testid={`${testIdPrefix}-me`}>Anda <b>#{me.rank}</b> · <b>{me.total}</b> FIX</span>}
          {top && <span className="mlb-c-top" data-testid={`${testIdPrefix}-top`}>Teratas: <b>{top.name}</b> · {top.total} FIX</span>}
          {!top && <span className="mlb-c-muted">Belum ada data</span>}
        </div>}
    </div>
    <button type="button" className="mlb-c-btn" onClick={onExpand} data-testid={`${testIdPrefix}-expand`}>Lihat Leaderboard &amp; Grafik</button>
  </section>;
}

export function LeaderboardMockupShopee({ testIdPrefix = "mockup-leaderboard", compact = false, meId = null, meName = "" }) {
  const [expanded, setExpanded] = useState(!compact);
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/shopee/mockup/leaderboard`, {
        params: { days }, withCredentials: true, timeout: 15_000,
      });
      setData(r?.data || null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setData(null);
      } else {
        setErr(e?.response?.data?.detail || e?.message || "Gagal memuat leaderboard");
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const names = (data.designers || []).map(d => d.name);
    return (data.dates || []).map((d, i) => {
      const row = { date: d, label: _shortDate(d) };
      for (const n of names) {
        const des = (data.designers || []).find(x => x.name === n);
        row[n] = des?.series?.[i]?.count ?? 0;
      }
      return row;
    });
  }, [data]);

  const xTickFormatter = useCallback((val, i) => {
    if (days === 30) return (i % 3 === 0) ? val : "";
    return val;
  }, [days]);

  const designers = data?.designers || [];
  const totalAll = designers.reduce((n, d) => n + (d.total || 0), 0);

  if (!expanded) return <CompactLeaderboard data={data} days={days} meId={meId} meName={meName} loading={loading} err={err}
    onExpand={() => setExpanded(true)} testIdPrefix={testIdPrefix}/>;

  return (
    <section className="mockup-leaderboard" data-testid={testIdPrefix}
             style={{
               background: "var(--card-bg, #f8fafc)",
               border: "1px solid var(--card-border, #e2e8f0)",
               borderRadius: 10, padding: "10px 12px", margin: "10px 0 14px",
               display: "flex", flexDirection: "column", gap: 10,
             }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14 }} data-testid={`${testIdPrefix}-title`}>
            Leaderboard Mockup Shopee
          </h3>
          <p className="muted small" style={{ margin: "2px 0 0", fontSize: 11 }}>
            Kompetisi sehat antar PIC Mockup Shopee ·{" "}
            {days === 0 ? "Semua Riwayat" : days === 1 ? "Hari ini" : `${days} hari`} (WIB)
          </p>
        </div>
        <div role="tablist" aria-label="Pilih periode" style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { v: 1, label: "Hari Ini" },
            { v: 7, label: "7 Hari" },
            { v: 30, label: "30 Hari" },
            { v: 0, label: "Semua Riwayat" },
          ].map(({ v, label }) => (
            <button key={v} type="button" role="tab" aria-selected={days === v}
              onClick={() => setDays(v)}
              data-testid={`${testIdPrefix}-period-${v}`}
              style={{
                padding: "4px 10px", fontSize: 12, borderRadius: 6, cursor: "pointer",
                border: "1px solid " + (days === v ? "#0f172a" : "#cbd5e1"),
                background: days === v ? "#0f172a" : "transparent",
                color: days === v ? "#fff" : "inherit", fontWeight: 600,
              }}>{label}</button>
          ))}
          {compact && <button type="button" onClick={() => setExpanded(false)} data-testid={`${testIdPrefix}-collapse`}
            style={{ padding: "4px 10px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: "1px solid #cbd5e1", background: "transparent", color: "inherit", fontWeight: 600 }}>
            Tutup</button>}
        </div>
      </div>

      {loading && <div className="muted small">Memuat…</div>}
      {err && <div className="warn-box small" data-testid={`${testIdPrefix}-err`}>{err}</div>}

      {!loading && !err && (
        <>
          <div style={{ display: "grid", gap: 6, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
               data-testid={`${testIdPrefix}-ranking`}>
            {designers.length === 0 && (
              <div className="muted small">Belum ada Desainer Mockup Shopee aktif.</div>
            )}
            {designers.map((d, idx) => (
              <div key={d.id || idx}
                   data-testid={`${testIdPrefix}-row-${d.id || idx}`}
                   style={{
                     display: "flex", alignItems: "center", gap: 8,
                     padding: "6px 8px", borderRadius: 8,
                     background: "var(--row-bg, #fff)",
                     border: "1px solid var(--card-border, #e2e8f0)",
                     borderLeft: `4px solid ${BAR_COLORS[idx % BAR_COLORS.length]}`,
                   }}>
                <RankBadge rank={d.rank} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {d.name}
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {days === 0
                      ? (d.rank === 1 ? "Total kumulatif" : `Selisih ${d.gap_from_top ?? 0} · total kumulatif`)
                      : (d.rank === 1
                          ? `Rata-rata ${d.average}/hari`
                          : `Selisih ${d.gap_from_top ?? 0} · rata-rata ${d.average}/hari`)
                    }
                  </div>
                  {d.revisi > 0 && (
                    <div style={{ fontSize: 10, color: "#b45309" }} data-testid={`${testIdPrefix}-revisi-${d.id || idx}`}>
                      ⚠ {d.revisi} hasil FIX sedang direvisi (tetap dihitung 1×)
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }} data-testid={`${testIdPrefix}-total-${d.id || idx}`}>
                    {d.total}
                  </div>
                  <div className="muted" style={{ fontSize: 10 }}>FIX</div>
                </div>
              </div>
            ))}
          </div>

          {designers.length > 0 && days !== 0 && (
            <div style={{ width: "100%", height: 180, minWidth: 0 }} data-testid={`${testIdPrefix}-chart`}>
              {totalAll === 0 ? (
                <div className="muted small" style={{ padding: 24, textAlign: "center" }}>
                  Belum ada mockup FIX pada periode ini.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 6, bottom: 4, left: 0 }}>
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "currentColor" }}
                      tickLine={false}
                      axisLine={{ stroke: "currentColor", opacity: 0.3 }}
                      tickFormatter={xTickFormatter}
                      interval={0}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 10, fill: "currentColor" }}
                      tickLine={false}
                      axisLine={{ stroke: "currentColor", opacity: 0.3 }}
                      width={24}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(100,116,139,0.1)" }}
                      wrapperStyle={{ outline: "none" }}
                      contentStyle={{
                        background: "rgba(15,23,42,0.95)",
                        border: "1px solid rgba(148,163,184,0.4)",
                        borderRadius: 6, padding: "6px 10px",
                        fontSize: 11, color: "#f8fafc",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                      }}
                      itemStyle={{ color: "#f8fafc" }}
                      labelStyle={{ color: "#cbd5e1", fontSize: 10, marginBottom: 2 }}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""}
                      formatter={(val, name) => [`${val} FIX`, name]}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                      iconType="square"
                      iconSize={10}
                    />
                    {designers.map((d, idx) => (
                      <Bar key={d.id || idx} dataKey={d.name} name={d.name}
                           fill={BAR_COLORS[idx % BAR_COLORS.length]} radius={[3, 3, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default LeaderboardMockupShopee;
